import axios from 'axios';
import cookieReader from 'js-cookie';
import { getMainApiUrl } from './UrlUtils';
import { attachDiagnostics } from '@xarvio/frontend-core-platform/lib/diagnostics/diagnosticAxiosInterceptor';

const axiosInstance = axios.create({
  baseURL: getMainApiUrl(),
  responseType: 'json',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const handleUnauthorized = () => {
  // Remove auth cookies
  cookieReader.remove(import.meta.env.VITE_SECURITY_TOKEN_NAME, {
    domain: import.meta.env.VITE_TOKEN_DOMAIN,
  });
  cookieReader.remove(import.meta.env.VITE_GIGYA_LOGIN_TOKEN_NAME, {
    domain: import.meta.env.VITE_TOKEN_DOMAIN,
  });

  // Best-effort redirect to login without depending on AppRouter/Redux.
  // If a locale is present in the URL, keep it.
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const maybeLocale = pathParts[0];
  const localePrefix =
    maybeLocale && maybeLocale.includes('_') ? `/${maybeLocale}` : '';
  window.location.href = `${localePrefix}/login`;
};

// Add a request interceptor
axiosInstance.interceptors.request.use((config) => {
  const token =
    cookieReader.get(import.meta.env.VITE_SECURITY_TOKEN_NAME) || '';
  if (token) {
    config.headers[import.meta.env.VITE_API_DF_TOKEN_NAME] = token;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    // Do something with response data
    return response.data;
  },
  async (error) => {
    const { status, data } = error.response || {};

    if (status === 401) {
      handleUnauthorized();
    }

    if (typeof data === 'string') {
      return Promise.reject({
        status,
        message: data,
      });
    }

    // Handle blob error responses (when responseType is 'blob' but server returns JSON error)
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsedData = JSON.parse(text);
        return Promise.reject({
          status,
          ...parsedData,
        });
      } catch {
        return Promise.reject({
          status,
        });
      }
    }

    // Do something with response error
    return Promise.reject({
      status,
      ...data,
    });
  },
);

attachDiagnostics(axiosInstance, 'api');

export default axiosInstance;
